import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { typeImageByName } from '../../components/upload/intake/rules';
import { captureSurfaceForClient } from '@bower/shared/constants/client';
import type { EquipmentStructuredData, PaginatedResponse } from '@bower/shared';
import { apiService } from '../../services/api.service';
import {
  createEquipment,
  getEquipment,
  listEquipment,
  retriggerEquipmentKnowledgeFetch,
  scanEquipmentPhoto,
  searchEquipmentOnline,
  updateEquipment,
  uploadEquipmentPhoto,
} from '../../services/api/equipment.api';
import type { CreateEquipmentBody } from '../../services/api/equipment.api';
import { unwrapApiResponse } from '../../services/api/http-client.types';
import { useWorkspaceId } from '../../contexts/WorkspaceContext';
import { queryKeys } from '../../lib/queryClient';
import { useInfinitePaginated } from './useInfinitePaginated';
import { safeAnalytics } from '../../utils/analytics';
import { currentBowerClient } from '../../utils/bowerClient';
import { ANALYTICS_EVENTS } from '../../constants/analytics';

export interface KnowledgeArtifact {
  id: string;
  title: string | null;
  metadata?: unknown;
  artifactType: string;
  createdAt: string;
}

export interface EquipmentItem {
  id: string;
  title: string | null;
  structuredData: EquipmentStructuredData | null;
  updatedAt: string;
  knowledgeArtifacts?: KnowledgeArtifact[];
  thumbnailUrl?: string;
  /**
   * True when this equipment has an effective_restriction row — i.e. it is
   * visible to the caller but restricted to specific people. Equipment IS an
   * Artifact (artifactType = EQUIPMENT), so it is inside the entity-ACL system
   * exactly like notes and attachments.
   */
  isRestricted?: boolean;
}

export interface EquipmentFilters {
  search?: string;
  limit?: number;
}

export function useInfiniteEquipment(filters: EquipmentFilters = {}) {
  const workspaceId = useWorkspaceId();

  return useInfinitePaginated<EquipmentItem>({
    queryKey: [...queryKeys.equipment.all(workspaceId), filters] as const,
    fetchPage: async ({ cursor, limit }) => {
      const params: Record<string, string | number> = { limit };
      if (filters.search) params.search = filters.search;
      if (cursor) params.cursor = cursor;

      const response = await listEquipment(apiService, params);
      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Failed to fetch equipment');
      }
      return response.data as unknown as PaginatedResponse<EquipmentItem>;
    },
    limit: filters.limit,
    enabled: !!workspaceId,
    staleTime: 30_000,
  });
}

export function useEquipment(id: string | undefined) {
  const workspaceId = useWorkspaceId();

  return useQuery({
    // Own root — see queryKeys.equipment.detail. Broad ['equipment'] sweeps
    // must not reach this query.
    queryKey: queryKeys.equipment.detail(id ?? ''),
    queryFn: async () => {
      if (!id) throw new Error('Equipment ID required');
      const response = await getEquipment(apiService, id);
      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Failed to fetch equipment');
      }
      return response.data;
    },
    enabled: !!id && !!workspaceId,
    staleTime: 30_000,
  });
}

export function useCreateEquipment() {
  const queryClient = useQueryClient();
  const workspaceId = useWorkspaceId();

  return useMutation({
    // Not the app's default single retry: a create is not idempotent, so a
    // retried POST after a lost response makes a second instrument, and a refusal
    // (a 4xx such as a name clash or a trashed parent) would be sent twice and
    // reach the user a couple of seconds late.
    retry: false,
    // Typed from the API body rather than re-declared inline, so a new field
    // can't silently exist on one side only.
    mutationFn: async (body: CreateEquipmentBody) => {
      const response = await createEquipment(apiService, body);
      // Throws ApiResponseError, which keeps the envelope's code, so a caller
      // can tell a refused destination (INVALID_PARENT, ENTITY_ACCESS_DENIED)
      // from any other failure. Same message as before.
      if (!unwrapApiResponse(response, 'Failed to create equipment')) {
        throw new Error('Failed to create equipment');
      }
      return response;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.equipment.all(workspaceId) });
      void queryClient.invalidateQueries({ queryKey: ['recents'] });
      safeAnalytics.track(ANALYTICS_EVENTS.EQUIPMENT_CREATED, {
        workspace_id: workspaceId,
        // Gives the equipment funnel an attributed terminal step — without it
        // a QR-originated create is indistinguishable from a typed one. The
        // body no longer carries a surface, so read the same fact off the
        // declared client: identical vocabulary (`desktop`/`mobile`), and it
        // can no longer disagree with the row the server writes.
        creation_source: captureSurfaceForClient(currentBowerClient()),
        has_make: !!variables.make,
        has_model: !!variables.model,
        has_photo: !!variables.photoUrl,
      } as any);
    },
  });
}

export function useUpdateEquipment() {
  const queryClient = useQueryClient();
  const workspaceId = useWorkspaceId();

  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      title?: string;
      make?: string;
      model?: string;
      serial?: string;
      location?: string;
      notes?: string;
    }) => updateEquipment(apiService, id, body),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.equipment.detail(variables.id),
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.equipment.all(workspaceId) });
    },
  });
}

export function useScanEquipmentPhoto() {
  return useMutation({
    mutationFn: (imageBase64: string) => scanEquipmentPhoto(apiService, imageBase64),
    onSuccess: () => {
      safeAnalytics.track(ANALYTICS_EVENTS.EQUIPMENT_SCANNED, {} as any);
    },
  });
}

export function useRetriggerEquipmentKnowledge() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => retriggerEquipmentKnowledgeFetch(apiService, id),
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.equipment.detail(id) });
    },
  });
}

export function useUploadEquipmentPhoto() {
  const queryClient = useQueryClient();
  const workspaceId = useWorkspaceId();

  return useMutation({
    mutationFn: async ({ id, file }: { id: string; file: File }) => {
      // An untyped iPhone HEIC would be refused by the route's type filter.
      const response = await uploadEquipmentPhoto(apiService, id, typeImageByName(file));
      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Failed to upload equipment photo');
      }
      return response;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.equipment.detail(variables.id),
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.equipment.all(workspaceId) });
    },
  });
}

export function useSearchEquipmentOnline() {
  return useMutation({
    mutationFn: (query: string) => searchEquipmentOnline(apiService, query),
  });
}
